import { GridDataService } from './grid-data.service';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { GridQueryUtils } from '../../utility/grid-query-utils';
import { Subscription } from 'rxjs';
import { MessengerService } from './../messenger.service';
import { sortBy } from 'lodash';
import { MessageHandlerService } from '../../utility/message-handler.service';

@Injectable()
export class EventListService extends GridDataService implements OnDestroy
{
    public eventTypes: Array<{ text: string; key: string; value: string; icon: string }> = [
        {
            text: 'Discharge', key: 'EVENT-TYPE-DISCHARGE', value: 'DISCHARGE',
            icon: 'fas fa-hospital'
        },
        {
            text: 'Admission', key: 'EVENT-TYPE-ADMISSION', value: 'ADMISSION',
            icon: 'fas fa-procedures'
        },
        {
            text: 'Medication Order', key: 'EVENT-TYPE-MEDICATION-ORDER', value: 'MEDICATION_ORDER',
            icon: 'fas fa-medkit'
        },
        {
            text: 'Medication Fill', key: 'EVENT-TYPE-MEDICATION-FILL', value: 'MEDICATION_FILL',
            icon: 'fas fa-check-circle'
        },
        {
            text: 'Appointment', key: 'EVENT-TYPE-APPOINTMENT', value: 'APPOINTMENT',
            icon: 'fas fa-calendar-plus'
        },
        {
            text: 'Encounter', key: 'EVENT-TYPE-ENCOUNTER', value: 'ENCOUNTER',
            icon: 'fas fa-plus-square'
        },
        {
            text: 'Lab Result', key: 'EVENT-TYPE-LAB-RESULT', value: 'LAB_RESULT',
            icon: 'fas fa-vial'
        },
        {
            text: 'Lab Order', key: 'EVENT-TYPE-LAB-ORDER', value: 'LAB_ORDER',
            icon: 'fas fa-vial'
        },
        {
            text: 'Facility Transfer', key: 'EVENT-TYPE-FACILITY-TRANSFER', value: 'FACILITY_TRANSFER',
            icon: 'fas fa-hospital-user'
        },
        {
            text: 'Referral', key: 'EVENT-TYPE-REFERRAL', value: 'REFERRAL',
            icon: 'fas fa-hospital-user'
        },
        {
            text: 'Referral Cancellation', key: 'EVENT-TYPE-REFERRAL-CANCELLATION', value: 'REFERRAL_CANCELLATION',
            icon: 'fas fa-hospital-user event-type-referral-cancellation'
        }
    ];

    public encounterTypes: Array<{ text: string; key: string; value: string }> = [
        { text: 'Emergency', key: 'ENCOUNTER-TYPE-EMERGENCY', value: 'EMERGENCY' },
        { text: 'Inpatient', key: 'ENCOUNTER-TYPE-INPATIENT', value: 'INPATIENT' },
        { text: 'Outpatient', key: 'ENCOUNTER-TYPE-OUTPATIENT', value: 'OUTPATIENT' },
    ];

    public appointmentStatuses: Array<{ text: string; key: string; value: string }> = [
        { text: 'Scheduled', key: 'APPOINTMENT-STATUS-SCHEDULED', value: 'SCHEDULED' },
        { text: 'Missed', key: 'APPOINTMENT-STATUS-MISSED', value: 'MISSED' },
        { text: 'Cancelled', key: 'APPOINTMENT-STATUS-CANCELLED', value: 'CANCELLED' },
        { text: 'Confirmed', key: 'APPOINTMENT-STATUS-CONFIRMED', value: 'CONFIRMED' },
    ];

    private messagingLanguageChangedSubscription: Subscription;

    constructor(http: HttpClient, translate: TranslateService, messageHandlerService: MessageHandlerService,
        messengerService: MessengerService)
    {
        super(http, '/events', translate, messageHandlerService);
        this.loadLocalizations(translate);
        this.messagingLanguageChangedSubscription = messengerService.receiveMessage().subscribe({
            next: (message) =>
            {
                if (message && message.type && message.type === 'languageChanged')
                {
                    this.loadLocalizations(translate);
                }
            }
        });
    }

    public ngOnDestroy(): void
    {
        if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
    }

    /**
     * Get the event type icon to use
     *
     * @param eventType Event Type
     * @returns string Icon class
     */
    public getEventTypeIcon(eventType: string): string
    {
        let iconClass = '';
        const event = this.eventTypes.find(x => x.value === eventType);
        if (event !== undefined)
        {
            iconClass = `${event.icon} fa-1_5x`;
        }
        return iconClass;
    }

    /**
     * Get the event type description to use
     *
     * @param eventType Event Type
     * @returns string Event Type description
     */
    public getEventTypeText(eventType: string): string
    {
        let text = eventType;
        const event = this.eventTypes.find(x => x.value === eventType);
        if (event !== undefined)
        {
            text = event.text;
        }
        return text;
    }

    /**
     * Get the encounter type description to use
     *
     * @param encounterType Encounter Type
     * @returns string Encounter Type description
     */
    public getEncounterTypeText(encounterType: string): string
    {
        let text = encounterType;
        const aType = this.encounterTypes.find(x => x.value === encounterType);
        if (aType !== undefined)
        {
            text = aType.text;
        }
        return text;
    }

    /**
     * Get the appointment status description to use
     *
     * @param appointmentStatus Appointment Status
     * @returns string Appointment Status description
     */
    public getAppointmentStatusText(appointmentStatus: string): string
    {
        let text = appointmentStatus;
        const aStatus = this.appointmentStatuses.find(x => x.value === appointmentStatus);
        if (aStatus !== undefined)
        {
            text = aStatus.text;
        }
        return text;
    }

    public queryByStateAndSource(state: any, source: string, queryCallback?: any,
        errorCallback?: any, finallyCallback?: any): void
    {
        this.loading = true;
        let queryStr = GridQueryUtils.generateQueryString(state, this.route);
        queryStr = queryStr + '&source=' + source;
        this.get(queryStr).subscribe({
            next: (x) =>
            {
                super.next(x);
                if (queryCallback !== undefined)
                {
                    queryCallback(x);
                }
            },
            error: (errorResponse) =>
            {
                this.loading = false;
                // If callback is set, it must handle error
                if (errorCallback !== undefined)
                {
                    errorCallback(errorResponse);
                }
                else
                {
                    this.messageHandlerService.displayError(errorResponse);
                }
            },
            complete: () =>
            {
                this.loading = false;
                if (finallyCallback !== undefined)
                {
                    finallyCallback();
                }
            }
        });
    }

    /**
     * Load localizations
     */
    private loadLocalizations(translate: TranslateService): void
    {
        // Set the event types from localization
        const eventTypesTranslationKeys = this.eventTypes.map(a => a.key);
        translate.get(eventTypesTranslationKeys).subscribe({
            next: (res: string) =>
            {
                for (let i = 0; i < this.eventTypes.length; i++)
                {
                    const key = 'EVENT-TYPE-' + this.eventTypes[i].value.replace('_', '-');
                    this.eventTypes[i].text = res[key];
                }
                this.eventTypes = sortBy(this.eventTypes, ['text']);
            }
        });

        const encounterTypesTranslationKeys = this.encounterTypes.map(a => a.key);
        translate.get(encounterTypesTranslationKeys).subscribe({
            next: (res: string) =>
            {
                for (let i = 0; i < this.encounterTypes.length; i++)
                {
                    const key = 'ENCOUNTER-TYPE-' + this.encounterTypes[i].value.replace('_', '-');
                    this.encounterTypes[i].text = res[key];
                }
                this.encounterTypes = sortBy(this.encounterTypes, ['text']);
            }
        });

        const appointmentStatusesTranslationKeys = this.appointmentStatuses.map(a => a.key);
        translate.get(appointmentStatusesTranslationKeys).subscribe({
            next: (res: string) =>
            {
                for (let i = 0; i < this.appointmentStatuses.length; i++)
                {
                    const key = 'APPOINTMENT-STATUS-' + this.appointmentStatuses[i].value.replace('_', '-');
                    this.appointmentStatuses[i].text = res[key];
                }
                this.appointmentStatuses = sortBy(this.appointmentStatuses, ['text']);
            }
        });
    }
}
